import React, { useState, useEffect } from "react";
import { Form, Dropdown, DropdownButton, Row } from "react-bootstrap";
import Select from "react-select";
import DataTable from "react-data-table-component";
import SessionHelper from "../../helpers/SessionHelper";
import { debounce } from "lodash";
import StorageHelper from "../../helpers/StorageHelper";
import { REPORTS } from "../../configurations/Urls";
import UserReportService from "../../api-service/UserReportService";
import {
  INCOMING_TOKEN_HAS_EXPIRED,
  SESSION_HAS_EXPIRED,
} from "../../configurations/MessageConstants";
import Lottie from "react-lottie";
import loadingAveragesLottie from "../../lotties/farmer.json";
import LoadingOverlay from "../common/LoadingOverlay";
import {
  EMPTY,
  SESSION_EXPIRED,
  SESSION_EXPIRED_MESSAGE,
} from "../../configurations/Constants";
import { StatusCodes } from "../../configurations/status-codes";

const SearchBox = ({ onSearch, value }) => (
  <Form.Control
    type="text"
    defaultValue={value}
    name="searchValue"
    onChange={onSearch}
    placeholder="Search"
    className="form-control form-control-lg me-2 w-25"
  />
);

const Export = ({ onExport }) => (
  <DropdownButton
    className="seasonal-averages-export-btn"
    id="dropdown-basic-button"
    title={
      <span>
        Export <i className="fa fa-download ms-2" aria-hidden="true"></i>
      </span>
    }
  >
    <Dropdown.Item onClick={() => onExport("pdf")}>Export as PDF</Dropdown.Item>
    <Dropdown.Item onClick={() => onExport("xlsx")}>
      Export as XLSX
    </Dropdown.Item>
  </DropdownButton>
);

function SeasonalAveragesTable({ selectedYear, setSelectedYear, yearArray }) {
  const [searchValue, setSearchValue] = useState(null);
  const [column, setColumn] = useState(null);
  const [columnOrder, setColumnOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [exportURL, setExportURL] = useState(EMPTY);
  const [data, setData] = useState([]);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: loadingAveragesLottie,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const columns = [
    {
      id: 1,
      name: "Year",
      selector: (row) => row.crop_year,
      sortable: true,
      reorder: true,
      value: "crop_year",
    },
    {
      id: 2,
      name: "Field Name",
      selector: (row) => row.fields,
      sortable: true,
      reorder: true,
      value: "fields",
    },
    {
      id: 3,
      name: "Acres",
      selector: (row) => row.field_acres,
      sortable: true,
      reorder: true,
      value: "field_acres",
    },
    {
      id: 4,
      name: "Inches",
      selector: (row) => row.inches,
      sortable: true,
      reorder: true,
      value: "inches",
    },
    {
      id: 5,
      name: "Area Average",
      selector: (row) => row.area_avg_inches,
      sortable: true,
      reorder: true,
      width: "8rem",
      value: "area_avg_inches",
    },
    {
      id: 6,
      name: "Wells",
      selector: (row) => row.well_ids,
      sortable: true,
      reorder: true,
      value: "well_ids",
      width: "15rem",
    },
    {
      id: 7,
      name: "Area",
      selector: (row) => row.area,
      sortable: true,
      reorder: true,
      value: "area",
    },

    {
      id: 8,
      name: "Crops",
      selector: (row) => row.crops,
      sortable: true,
      reorder: true,
      value: "crops",
      width: "25rem",
    },
  ];

  useEffect(() => {
    setLoading(true);
    fetchSeasonalAverages(
      currentPage,
      perPage,
      selectedYear,
      searchValue,
      column,
      columnOrder
    );
  }, [currentPage, perPage, selectedYear, searchValue, column, columnOrder]);

  const fetchSeasonalAverages = async (
    page,
    size,
    selectedYear,
    searchValue,
    column,
    columnOrder
  ) => {
    setLoading(true);
    let params = `?current_pg=${page}&rows_per_pg=${size}`;
    if (selectedYear) params = `${params}&year=${selectedYear.value}`;
    if (searchValue) params = `${params}&search_text=${searchValue}`;
    if (column !== null) params = `${params}&column=${column}`;
    if (columnOrder !== null) params = `${params}&column_order=${columnOrder}`;

    const url = `${REPORTS}/year${params}`;
    const exporturl = `${REPORTS}/year/export${params}`;

    try {
      const response = await UserReportService.getSeasonalAverages(url);
      let reportData = response.data.data;
      if (reportData) {
        setExportURL(exporturl);
        setData(reportData.data);
        setTotalRows(reportData.totalRows);
        setLoading(false);
      }
    } catch (error) {
      const responseData = error.response.data;
      if (
        error.response.status === StatusCodes.UNAUTHORIZED &&
        responseData.message.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(SESSION_EXPIRED, true);
        StorageHelper.setItem(SESSION_EXPIRED_MESSAGE, SESSION_HAS_EXPIRED);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      }
    }
  };

  const handleSearchValue = async (e) => {
    await debouncedSearch(e.target.value);
  };

  const debouncedSearch = debounce(async (value) => {
    setSearchValue(value);
  }, 800);

  const handleSort = async (column, sortDirection) => {
    setColumn(column.value);
    setColumnOrder(sortDirection);
  };

  const downloadCSV = async (url) => {
    setOverlayLoading(true);

    try {
      const response = await UserReportService.exportSeasonalAverages(url);
      const filenameRegex = /filename="(.*)"/;
      const matches = filenameRegex.exec(
        response.headers["content-disposition"]
      );
      const fileName = matches && matches[1] ? matches[1] : "end_of_year.xlsx";
      let blob = await response.data;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      setOverlayLoading(false);
    } catch (error) {
      setOverlayLoading(false);
      const responseData = error.response.data;
      if (
        error.response.status === StatusCodes.UNAUTHORIZED &&
        responseData.message.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(SESSION_EXPIRED, true);
        StorageHelper.setItem(SESSION_EXPIRED_MESSAGE, SESSION_HAS_EXPIRED);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      }
    }
  };

  const HandleSelectedYear = (e) => {
    setSelectedYear(e);
  };

  return (
    <div className="seasonal-averages p-3 ms-2 me-2">
      <LoadingOverlay
        showSpinner={true}
        active={overlayLoading}
        text={EMPTY}
        spinnerType={"defaultExportOptions"}
      />
      <div className="d-flex mb-2">
        <h3 className="ms-3 mt-2">Seasonal Averages</h3>
        <div className="d-flex justify-content-end flex-grow-1 gap-2 align-items-center">
          <Select
            name="yearList"
            options={yearArray}
            value={selectedYear}
            className="seasonal-averages-filter basic-multi-select"
            onChange={HandleSelectedYear}
          />
          <SearchBox onSearch={(e) => handleSearchValue(e, searchValue)} />
          <Export
            onExport={(item) => downloadCSV(`${exportURL}&type=${item}`)}
          />
        </div>
      </div>
      <Row>
        <DataTable
          columns={columns}
          data={data}
          progressPending={loading}
          pagination
          paginationServer
          sortServer
          onSort={handleSort}
          paginationTotalRows={totalRows}
          paginationDefaultPage={currentPage}
          onChangeRowsPerPage={setPerPage}
          onChangePage={setCurrentPage}
          progressComponent={
            <Lottie options={defaultOptions} height={250} width={250} />
          }
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Row>
    </div>
  );
}

export default SeasonalAveragesTable;
